import { CardBlock, FieldBlock, InputBlock } from '@coast/schemas';
import { ReactElement } from 'react';
import { View } from '@shared/components/View';
import { API } from '@shared/types/api';
import { InputBlockComponent } from './InputBlockComponent';
import { NoCodeBlockContainer } from './NoCodeBlockContainer';
import { getFieldValue, nonEmptyBlocks } from './blockRenderVisibility';
import { getBlocksToRender } from './getBlocksToRender';
import { MBButtonBlock } from './lib/button';
import { MBCardsBlock } from './lib/cards';
import { MBFieldChannelBlock } from './lib/channel';
import { MBChatThreadBlock } from './lib/chatThread';
import { MBChatThreadInputBlock } from './lib/chatThreadInput';
import { MBDateRangeBlock } from './lib/dateRange';
import { MBFieldBlock } from './lib/field';
import { MBFieldAssigneeBlock } from './lib/fieldAssignee';
import { MBFieldFileBlock } from './lib/fieldFile';
import { SubformsFieldBlock } from './lib/fieldSubforms/SubformsFieldBlock';
import { MBImageBlock } from './lib/image';
import { MBInCardsBlock } from './lib/inCards';
import { MBCardInputBlockDateRange } from './lib/inputDateRange';
import { MBCardInputBlockQuestion } from './lib/inputQuestion';
import { MBSeenByBlock } from './lib/seenBy';
import { MBSequenceNumberBlock } from './lib/sequenceNumber';
import { MBTagsBlock } from './lib/tags';
import { CommonFields, Fields } from './types';

export function renderFieldBlock(
  block: FieldBlock,
  key: any,
  value: any,
  onFieldPress: () => void,
): ReactElement {
  switch (block.fieldType) {
    case 'channel':
      return <MBFieldChannelBlock key={key} block={block} onPress={onFieldPress} />;
    case 'assignee':
      if (!value) {
        return <View key={key} />;
      }
      return <MBFieldAssigneeBlock key={key} block={block} value={value} onPress={onFieldPress} />;
    case 'file':
      if (!value?.length) {
        return <View key={key} />;
      }
      return <MBFieldFileBlock key={key} value={value} label={block.label} />;
    case 'questionTextArea':
    case 'questionCheckbox':
    case 'questionSingleSelect':
      /*
      Render a question input block in completion mode on the details (and read-only)
      views so users can complete questions without having to navigate to the edit form.
      We may choose to change how this works in the future.
       */
      return (
        <MBCardInputBlockQuestion
          block={{ ...block, mode: 'completion' } as any as InputBlock}
          key={key}
        />
      );
    case 'subforms':
      return <SubformsFieldBlock key={key} block={block} value={value} />;
    default:
      return <MBFieldBlock key={key} block={block} value={value} onPress={onFieldPress} />;
  }
}

export function renderBlockIfNotEmpty(
  block: CardBlock,
  card: API.CardFragment,
  fields: CommonFields & Fields,
  canReadThread: boolean,
  canCreateThread: boolean,
  canUpdateCard: boolean,
  printing: boolean,
  onFieldPress?: () => void,
): ReactElement {
  const isBlockEmpty = !nonEmptyBlocks(
    fields,
    canReadThread,
    canCreateThread,
    canUpdateCard,
    printing,
  )(block);

  if (isBlockEmpty) {
    // @ts-expect-error -- strict migration
    return null;
  }
  return renderBlock(block, card, fields, onFieldPress, 0);
}

export function renderBlock(
  block: CardBlock,
  card: API.CardFragment,
  fields: CommonFields & Fields,
  onFieldPress: VoidFunction | undefined,
  position: number,
  workflowTemplateId?: string,
): ReactElement {
  switch (block.type) {
    case 'sequenceNumber':
      // @ts-expect-error -- strict migration
      return <MBSequenceNumberBlock key={position} sequenceNumber={card.sequenceNumber} />;
    case 'cards':
      return (
        <MBCardsBlock
          key={position}
          block={block}
          onPress={onFieldPress}
          relatedCards={card.relatedCards}
        />
      );
    case 'in-cards':
      return (
        <MBInCardsBlock key={position} block={block} onPress={onFieldPress} cardId={card.id} />
      );
    case 'tags':
      return <MBTagsBlock key={position} block={block} onPress={onFieldPress} />;
    case 'button':
      return <MBButtonBlock key={position} block={block} card={card} />;
    case 'input':
      return (
        <InputBlockComponent
          key={position}
          block={block}
          // @ts-expect-error -- strict migration
          onFieldPress={onFieldPress}
          value={getFieldValue(fields, block)}
          workflowTemplateId={workflowTemplateId}
        />
      );
    case 'date-range-input':
      return <MBCardInputBlockDateRange key={position} block={block} />;
    case 'date-range': {
      const startDateFieldName = block.startDateFieldName ?? 'startDate';
      const endDateFieldName = block.endDateFieldName ?? 'endDate';
      return (
        <MBDateRangeBlock
          key={position}
          startDate={fields[startDateFieldName]}
          endDate={fields[endDateFieldName]}
          block={block}
          onPress={onFieldPress}
        />
      );
    }
    case 'image':
      return <MBImageBlock key={position} url={getFieldValue(fields, block)} />;
    case 'field':
      // @ts-expect-error -- strict migration
      return renderFieldBlock(block, position, getFieldValue(fields, block), onFieldPress);
    case 'seen-by':
      // Note(fant): this assumes that there's always a chat thread block
      // where this is used. Otherwise we have no way of knowing what
      // users have seen a card details.
      return <MBSeenByBlock key={position} threadId={(card as API.CardFragment).threadId} />;
    case 'chat-thread':
      return <MBChatThreadBlock key={position} threadId={(card as API.CardFragment).threadId} />;
    case 'chat-thread-input':
      return <MBChatThreadInputBlock key={position} cardId={card.id} />;
    case 'no-code':
      return (
        <NoCodeBlockContainer
          key={position}
          componentId={block.componentId}
          fields={fields}
          // @ts-expect-error -- strict migration
          workflowTemplateId={card.workflowTemplateId ?? workflowTemplateId}
        />
      );
    default:
      // @ts-expect-error -- strict migration
      return null;
  }
}

export function renderNonEmptyBlocks(
  blocks: CardBlock[],
  card: API.CardFragment,
  fields: CommonFields & Fields,
  canReadThread: boolean,
  canCreateThread: boolean,
  canUpdateCard: boolean,
  printing: boolean,
  onFieldPress: VoidFunction | undefined,
  workflowTemplateId?: string,
): ReactElement[] {
  if (blocks.length === 0) {
    // @ts-expect-error -- strict migration
    return null;
  }
  return getBlocksToRender(
    blocks,
    card,
    fields,
    canReadThread,
    canCreateThread,
    canUpdateCard,
    printing,
  ).map((block, idx) => {
    const position = idx + 1;
    return renderBlock(block, card, fields, onFieldPress, position, workflowTemplateId);
  });
}
